import { defineStore } from "pinia";
import { fetchCurrentUser } from "@/services/authService";
import { unsubscribeFromMercure } from '@/services/mercure';
import { useMessaging } from "@/composables/useMessaging";

// Tenant refs show up as plain IDs, IRIs ("/tenants/{id}"), or embedded
// objects depending on the endpoint - normalize them all down to a bare id.
const extractTenantId = (input: unknown): string => {
  const fromString = (raw: string): string =>
    raw.includes('/') ? raw.split('/').filter(Boolean).pop() ?? '' : raw;
  if (typeof input === 'string') return fromString(input);
  if (input && typeof input === 'object') {
    const rec = input as Record<string, unknown>;
    const id =
      (typeof rec.tenantId === 'string' && rec.tenantId) ||
      (typeof rec.id === 'string' && rec.id) ||
      (typeof rec['@id'] === 'string' && (rec['@id'] as string)) ||
      '';
    return fromString(id);
  }
  return '';
};

export const useAuthStore = defineStore("auth", {
  state: () => ({
    token: localStorage.getItem("access_token") || "",
    email: localStorage.getItem("email") || "",
    sub: localStorage.getItem("user_sub") || "",
    user: null as null | {
      roles: string[];
      firstName: string;
      lastName: string;
      email: string;
      orgAdmin?: boolean;
      tenantOwner?: boolean;
      tenantAdmin?: boolean;
      technician?: boolean;
      tenantMember?: boolean;
      tenantMemberships?: unknown[];
      [key: string]: any;
    },
    // mercureUnsubscribe: null as null | (() => void),
  }),

  getters: {
    isLoggedIn: (state) => !!state.token,
    // Organization-Admin: full access to everything (tenants, users, devices).
    // The backend is the only place that decides who gets ROLE_ORG_ADMIN.
    isOrgAdmin: (state) => (state.user?.roles ?? []).includes('ROLE_ORG_ADMIN'),
    // Tenant Owner: read-only access to their own tenant, manages tenant members/devices.
    isTenantOwner: (state) => !!state.user?.tenantOwner,
    // Tenant Admin: same UI access as Tenant Owner.
    isTenantAdmin: (state) => !!state.user?.tenantAdmin,
    // Technician: no admin UI access, but sees own + tenant-owned devices on the Home view.
    isTechnician: (state) => !!state.user?.technician,
    // Whether the user belongs to any tenant at all.
    isTenantMember: (state) => !!state.user?.tenantMember,
    // Whether the user can reach the /admin section at all (Org-Admin, Tenant
    // Owner and Tenant Admin can; Technician and End User cannot).
    hasAdminAccess(): boolean {
      return this.isOrgAdmin || this.isTenantOwner || this.isTenantAdmin;
    },
    // Devices admin section is Org-Admin only - Tenant Owner/Admin can reach
    // /admin (tenants, users) but not the devices pages.
    canAccessAdminDevices(): boolean {
      return this.isOrgAdmin;
    },
    // Users admin section (list of ALL org users) is Org-Admin only - Tenant
    // Owner/Admin manage their own tenant's members from the tenant overview
    // page instead.
    canAccessAdminUsers(): boolean {
      return this.isOrgAdmin;
    },
    // rbac 3.1: the system status is an Organization Admin interface only.
    canSeeSystemStatus(): boolean {
      return this.isOrgAdmin;
    },
    // UV7, UV8, UV10: the Organization Admin finds devices in the device
    // management, tenant members get the extended home screen.
    homeVariant(): 'org_admin' | 'extended' | 'standard' {
      if (this.isOrgAdmin) return 'org_admin';
      if (this.isTenantMember) return 'extended';
      return 'standard';
    },
    myTenantId: (state) => extractTenantId(state.user?.tenantMemberships?.[0]),
    fullName: (state) => state.user ? `${state.user.firstName} ${state.user.lastName}` : '',
  },

  actions: {
    setToken(token: string, sub?: string) {
      this.token = token;
      this.sub = sub ?? '';
      localStorage.setItem('access_token', token);
      if (sub) {
        localStorage.setItem('user_sub', sub);
      }
    },

    setUser(user: any) {
      this.user = user;
    },

    setEmail(email: string) {
      this.email = email;
      localStorage.setItem('email', email);
    },

    logout() {
      this.token = '';
      this.user = null;
      this.email = '';
      this.sub = '';
      localStorage.removeItem('access_token');
      localStorage.removeItem('email');
      localStorage.removeItem('user_sub');

      // Admin list search filters are session-scoped but must not leak to the
      // next account that logs in within the same browser tab.
      sessionStorage.removeItem('lec.admin.tenants.search');
      sessionStorage.removeItem('lec.admin.users.search');
      sessionStorage.removeItem('lec.admin.devices.search');

      // if (this.mercureUnsubscribe) {
      //   try {
      //     this.mercureUnsubscribe();
      //   } catch (e) { 
      //     console.warn("Error unsubscribing from Mercure:", e);
      //   }
      //   this.mercureUnsubscribe = null;
      // }

      try {
        unsubscribeFromMercure();
      } catch (e) {
        console.warn("Error unsubscribing from Mercure:", e);
      }
    },

    clearToken() {
      this.token = '';
      localStorage.removeItem('access_token');
    },

    async fetchUserIfNeeded() {
      
      if (this.token && !this.user) {
        //console.log("Fetching user if needed");
        try {
          const user = await fetchCurrentUser();
          await this.setUser(user);
          await useMessaging().registerHandlers(user);
        } catch (error) {
          console.error("Error fetching user:", error);
          this.logout();
        }
      }
    },

    // UV13: takes over a changed role or membership without signing in again.
    // Returns false when the account can no longer be read; then the session
    // is ended, as on start-up.
    async refreshUser(): Promise<boolean> {
      try {
        this.setUser(await fetchCurrentUser());
        return true;
      } catch (error) {
        console.error("Error refreshing user:", error);
        this.logout();
        return false;
      }
    },

    async validateToken() {
      if (this.token) {
        return true;
      } else {
        this.logout();
        return false;
      }
    },
  },
});
